import { useCallback, useEffect, useMemo, useState } from "react";

import { UserType } from "../../../constants";
import { TaskConstants } from "../../../constants/workflow/TaskConstants";
import { TabsList, UpcomingMilestonePropsWithKey } from "../../../models";
import {
  getAggregateWorkflowTasks,
  getProjectAggregate,
  GetTaskBoardResponse,
  searchWorkflowTasks,
  SearchWorkflowTasksResponse,
} from "../../../service/query";
import { getTaskBoard } from "../../../service/query/QueryService.full";
import { Status } from "../../../service/Shared";
import { useAuth } from "../../../useAuth";
import { getProjectRoadmapRoute, getProjectTrackerRoute } from "../../../utils/routes";
import { ChartData, OverdueTaskProps } from "../../../widget";

interface UseProjectsReturnData {
  tabsList: TabsList;
  phaseChartData: ChartData;
  isLoading: boolean;
  currentUserType: UserType;
  upcomingMilestones: UpcomingMilestonePropsWithKey[];
  overdueTasks: OverdueTaskProps;
  isOverdueTasksPanelOpen: boolean;
  setIsOverdueTasksPanelOpen: (isOpen: boolean) => void;
  isUpcomingMilestonesPanelOpen: boolean;
  setIsUpcomingMilestonesPanelOpen: (isOpen: boolean) => void;
  workflow: GetTaskBoardResponse["results"][number] | undefined;
  selectedPanelTask: SearchWorkflowTasksResponse["results"][0] | undefined;
  showWorkflowTaskPanel: boolean;
  handleCloseWorkflowTaskPanel: () => void;
  handleRefresh: () => void;
  refreshTasks: boolean;
  setSelectedPanelTask: (task: SearchWorkflowTasksResponse["results"][0] | undefined) => void;
  setShowWorkflowTaskPanel: (show: boolean) => void;
}
export const useProjects = (): UseProjectsReturnData => {
  const { currentUserType, currentDeveloperUuid, currentDevelopmentManagerUuid } = useAuth();
  const [refreshTasks, setRefreshTasks] = useState(false);
  const [phaseChartData, setPhaseChartData] = useState<ChartData>({});
  const [isLoading, setIsLoading] = useState(true);
  const [upcomingMilestones, setUpcomingMilestones] = useState<UpcomingMilestonePropsWithKey[]>([]);
  const [overdueTasks, setOverdueTasks] = useState<OverdueTaskProps>();
  const [isOverdueTasksPanelOpen, setIsOverdueTasksPanelOpen] = useState(false);
  const [isUpcomingMilestonesPanelOpen, setIsUpcomingMilestonesPanelOpen] = useState(false);
  const [workflow, setWorkflow] = useState<GetTaskBoardResponse["results"][number]>();
  const [selectedPanelTask, setSelectedPanelTask] = useState<SearchWorkflowTasksResponse["results"][0]>();
  const [showWorkflowTaskPanel, setShowWorkflowTaskPanel] = useState(false);

  const handleCloseWorkflowTaskPanel = useCallback((): void => {
    setSelectedPanelTask(undefined);
    setShowWorkflowTaskPanel(false);
  }, []);

  const handleRefresh = useCallback((): void => {
    setRefreshTasks((prev) => !prev);
  }, []);

  const tabsList = [
    {
      label: "Project tracker",
      route: getProjectTrackerRoute(currentUserType),
    },
    {
      label: "Project roadmap",
      route: getProjectRoadmapRoute(currentUserType),
    },
  ];

  const { filterBy, filterValue } = useMemo(() => {
    if (currentDeveloperUuid) {
      return {
        filterBy: "developer",
        filterValue: currentDeveloperUuid,
      };
    }
    if (currentDevelopmentManagerUuid) {
      return {
        filterBy: "developmentManager",
        filterValue: currentDevelopmentManagerUuid,
      };
    }
    throw new Error("Organisation not currently linked to a developer or development manager");
  }, [currentDeveloperUuid, currentDevelopmentManagerUuid]);

  const fetchData = useCallback(async () => {
    const phaseAggregateResponse = await getProjectAggregate({
      filterBy,
      filterOperator: "eq",
      filterValue,
      groupBy: "cachedPhase",
      aggregation: "count",
      aggregate: "id",
    });

    if (phaseAggregateResponse.status === Status.Success && phaseAggregateResponse.data) {
      const phaseAggregateData = phaseAggregateResponse.data.reduce<ChartData>((previous, current) => {
        // eslint-disable-next-line no-param-reassign
        previous[current.key] = current.value;
        return previous;
      }, {});
      const phaseAggregateDataReordered = {
        Feasibility: phaseAggregateData.Feasibility ?? 0,
        Design: phaseAggregateData.Design ?? 0,
        Implementation: phaseAggregateData.Implementation ?? 0,
        "Ongoing monitoring": phaseAggregateData["Ongoing Monitoring"] ?? 0,
      };
      setPhaseChartData(phaseAggregateDataReordered);
    }
  }, []);

  useEffect(() => {
    fetchData().finally(() => {
      setIsLoading(false);
    });
  }, [fetchData]);

  const getAndSetOverdueTasks = useCallback(async (): Promise<void> => {
    const tasks = await getAggregateWorkflowTasks({
      activityUuid: null,
      phase: null,
      projectUuid: null,
      workflowUuid: null,
      dealUuid: null,
    });

    setOverdueTasks({
      amount: tasks.data?.overdueTasksCount ? tasks.data?.overdueTasksCount.toString() : "0",
      label: "Overdue tasks",
    });
  }, [refreshTasks]);

  const getAndSetUpcomingMilestones = useCallback(async (): Promise<void> => {
    const result = await searchWorkflowTasks({
      paging: {
        limit: 4,
        afterCursor: null,
        beforeCursor: null,
      },
      filter: {
        results: {
          isMilestone: {
            operator: "eq",
            value: true,
          },
          deadlineDate: {
            operator: "neq",
            value: null,
          },
          status: {
            operator: "neq",
            value: TaskConstants.TASK_STATUS_COMPLETE,
          },
        },
      },
      sort: [{ key: "deadlineDate" as "results.deadlineDate", direction: "asc" }],
    });
    if (result.status !== Status.Success || result.data === undefined) {
      return;
    }
    setUpcomingMilestones(
      result.data.results.map((task) => ({
        key: task.uuid,
        name: task.name,
        deadlineDate: task.deadlineDate ? task.deadlineDate : undefined,
        status: task.status,
        assigneeName: task.assignee?.name ?? undefined,
        project: task.workflowObjectName,
        // eslint-disable-next-line no-alert
        onClick: () => {
          setSelectedPanelTask(task);
          setShowWorkflowTaskPanel(true);
        },
      }))
    );
  }, [refreshTasks]);

  useEffect(() => {
    getAndSetOverdueTasks();
    getAndSetUpcomingMilestones();
  }, [refreshTasks]);

  const getAndSetWorkflow = useCallback(async (): Promise<void> => {
    const res = await getTaskBoard({
      showComplete: true,
      paging: {
        afterCursor: null,
        beforeCursor: null,
        limit: 1,
      },
      filter: {
        results: {
          uuid: {
            operator: "eq",
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            value: selectedPanelTask!.workflowUuid,
          },
        },
      },
    });
    if (res.status === Status.Success && res.data) {
      setWorkflow(res.data.results[0]);
    }
  }, [selectedPanelTask?.workflowUuid]);

  useEffect(() => {
    if (selectedPanelTask?.workflowUuid) {
      getAndSetWorkflow();
    }
  }, [selectedPanelTask?.workflowUuid]);

  return {
    tabsList,
    phaseChartData,
    isLoading,
    currentUserType,
    upcomingMilestones,
    overdueTasks: overdueTasks || { amount: "0", label: "Overdue tasks" },
    isOverdueTasksPanelOpen,
    setIsOverdueTasksPanelOpen,
    isUpcomingMilestonesPanelOpen,
    setIsUpcomingMilestonesPanelOpen,
    workflow,
    selectedPanelTask,
    showWorkflowTaskPanel,
    handleCloseWorkflowTaskPanel,
    handleRefresh,
    refreshTasks,
    setSelectedPanelTask,
    setShowWorkflowTaskPanel,
  };
};
